import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Search, SearchX, ChevronLeft, ChevronRight, Megaphone, ArrowRight, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { readingTimeMinutes } from "@/lib/reading-time";
import { formatTag, slugifyTag } from "@/lib/tags";
import scottAvatar from "@/assets/scott-avatar.webp";
import { transformSupabaseImage, supabaseSrcSet } from "@/lib/supabase-image";
import { DarkSectionBackdrop } from "@/components/DarkSectionBackdrop";
import { SparkleDivider } from "@/components/SparkleDivider";
import { LightSectionBackdrop } from "@/components/LightSectionBackdrop";

type Post = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  content: string;
  cover_image_url: string | null;
  tags: string[];
  published_at: string | null;
  category_id: string | null;
  secondary_category_id: string | null;
  category: { name: string; slug: string } | null;
  secondary_category: { name: string; slug: string } | null;
};

type Category = { id: string; name: string; slug: string };

export const Route = createFileRoute("/blog/")({
  loader: async () => {
    const [postsResult, categoriesResult] = await Promise.all([
      supabase
        .from("posts")
        .select(
          "id, slug, title, excerpt, content, cover_image_url, tags, published_at, category_id, secondary_category_id, category:categories!posts_category_id_fkey(name, slug), secondary_category:categories!posts_secondary_category_id_fkey(name, slug)",
        )
        .eq("status", "published")
        .order("published_at", { ascending: false }),
      supabase
        .from("categories")
        .select("id, name, slug")
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true }),
    ]);

    return {
      posts: (postsResult.data as Post[]) ?? [],
      categories: (categoriesResult.data as Category[]) ?? [],
    };
  },
  head: ({ loaderData }) => {
    const firstCoverPost = loaderData?.posts.find((post) => post.cover_image_url);
    const firstCoverRaw = firstCoverPost?.cover_image_url;
    const firstCover = firstCoverRaw
      ? transformSupabaseImage(firstCoverRaw, { width: 1280, format: "webp", quality: 78 })
      : null;
    const recent = (loaderData?.posts ?? []).slice(0, 10);
    return {
      meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },

        { title: "Field Notes | Scott Cunningham" },
        {
          name: "description",
          content:
            "Field notes by Scott Cunningham (@scottcbusiness) on new tech, cybersecurity, crypto, Web3, privacy, AI workflows, and digital systems.",
        },
        { property: "og:title", content: "Field Notes | Scott Cunningham" },
        {
          property: "og:description",
          content:
            "Field notes by Scott Cunningham (@scottcbusiness) on new tech, cybersecurity, crypto, Web3, privacy, AI workflows, and digital systems.",
        },
        { property: "og:url", content: "https://scottcbusiness.com/blog" },
        { name: "twitter:title", content: "Field Notes | Scott Cunningham" },
        ...(firstCoverPost
          ? [
              { property: "og:image", content: `https://scottcbusiness.com/api/og/${firstCoverPost.slug}.png` },
              { property: "og:image:width", content: "1200" },
              { property: "og:image:height", content: "630" },
              { property: "og:image:alt", content: `${firstCoverPost.title}, Field Notes by Scott Cunningham` },
              { name: "twitter:image", content: `https://scottcbusiness.com/api/og/${firstCoverPost.slug}.png` },
              { name: "twitter:image:alt", content: `${firstCoverPost.title}, Field Notes by Scott Cunningham` },
            ]
          : []),
        {
          name: "twitter:description",
          content:
            "Field notes by Scott Cunningham (@scottcbusiness) on new tech, cybersecurity, crypto, Web3, privacy, AI workflows, and digital systems.",
        },
      ],
      links: [
        { rel: "canonical", href: "https://scottcbusiness.com/blog" },
        ...(firstCover
          ? [{ rel: "preload", as: "image", href: firstCover, fetchPriority: "high" as const }]
          : []),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://scottcbusiness.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Field Notes",
                item: "https://scottcbusiness.com/blog",
              },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "@id": "https://scottcbusiness.com/blog#blog",
            name: "Field Notes",
            url: "https://scottcbusiness.com/blog",
            description:
              "Field notes by Scott Cunningham (@scottcbusiness) on new tech, cybersecurity, crypto, Web3, privacy, AI workflows, and digital systems.",
            author: {
              "@type": "Person",
              name: "Scott Cunningham",
              url: "https://scottcbusiness.com",
            },
            blogPost: recent.map((p) => ({
              "@type": "BlogPosting",
              headline: p.title,
              description: p.excerpt ?? undefined,
              url: `https://scottcbusiness.com/blog/${p.slug}`,
              datePublished: p.published_at ?? undefined,
              image: p.cover_image_url ?? undefined,
              author: { "@type": "Person", name: "Scott Cunningham" },
            })),
          }),
        },
      ],
    };
  },
  component: BlogIndex,
});

function BlogIndex() {
  const { posts, categories } = Route.useLoaderData() as { posts: Post[]; categories: Category[] };
  const [activeCat, setActiveCat] = useState<string | null>(null); // category id
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // Hydrate from ?q= on mount
  useEffect(() => {
    if (typeof window === "undefined") return;
    const q = new URLSearchParams(window.location.search).get("q") ?? "";
    if (q) {
      setSearchInput(q);
      setSearch(q);
    }
  }, []);

  // Debounce input -> active search + URL sync
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      if (typeof window !== "undefined") {
        const url = new URL(window.location.href);
        if (searchInput.trim()) url.searchParams.set("q", searchInput.trim());
        else url.searchParams.delete("q");
        window.history.replaceState(null, "", url.toString());
      }
    }, 200);
    return () => clearTimeout(t);
  }, [searchInput]);

  const visiblePosts = useMemo(() => {
    const byCat = activeCat
      ? posts.filter((p) => p.category_id === activeCat || p.secondary_category_id === activeCat)
      : posts;
    const q = search.trim().toLowerCase();
    if (!q) return byCat;
    return byCat.filter((p) => {
      const hay = [p.title, p.excerpt ?? "", ...(p.tags ?? [])].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [posts, activeCat, search]);

  const showFeatured = !activeCat && !search.trim() && visiblePosts.length > 1;
  const featured = showFeatured ? visiblePosts[0] : null;
  const gridPosts = showFeatured ? visiblePosts.slice(1) : visiblePosts;

  // Pagination: 4 grid posts per page. Pagination only appears when the
  // featured-card layout is active (no filter / no search) AND there are more
  // than 4 grid posts (i.e. more than 5 total published posts).
  const PAGE_SIZE = 4;
  const paginate = showFeatured && gridPosts.length > PAGE_SIZE;
  const totalPages = paginate ? Math.ceil(gridPosts.length / PAGE_SIZE) : 1;
  const [page, setPage] = useState(1);

  // Reset to page 1 when filter/search changes or pagination disappears
  useEffect(() => {
    setPage(1);
  }, [activeCat, search, paginate]);

  const currentPage = Math.min(page, totalPages);
  const pagedGridPosts = paginate
    ? gridPosts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
    : gridPosts;

  const goToPage = (p: number) => {
    const next = Math.max(1, Math.min(totalPages, p));
    setPage(next);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Keyboard ←/→ pagination (when not typing in an input/textarea)
  useEffect(() => {
    if (!paginate) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "ArrowLeft" && currentPage > 1) {
        e.preventDefault();
        goToPage(currentPage - 1);
      } else if (e.key === "ArrowRight" && currentPage < totalPages) {
        e.preventDefault();
        goToPage(currentPage + 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [paginate, currentPage, totalPages]);

  const highlight = (text: string) => {
    const q = search.trim();
    if (!q) return text;
    const safe = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = text.split(new RegExp(`(${safe})`, "ig"));
    return parts.map((p, i) =>
      i % 2 === 1 ? (
        <mark key={i} className="search-hl">
          {p}
        </mark>
      ) : (
        <span key={i}>{p}</span>
      ),
    );
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    const month = d.toLocaleString("en-US", { month: "long" });
    const day = d.getDate();
    const year = d.getFullYear();
    const j = day % 10,
      k = day % 100;
    const suffix =
      k >= 11 && k <= 13 ? "th" : j === 1 ? "st" : j === 2 ? "nd" : j === 3 ? "rd" : "th";
    return `${month} ${day}${suffix}, ${year}`;
  };

  return (
    <div className="blog-scope">
      {/* Intro section — cream + blob gradients, matching homepage hero */}
      <section className="relative isolate overflow-hidden border-b border-border grain">
        <LightSectionBackdrop variant="abstract" />
        <div
          className="blob float-slow -left-32 -top-24 h-[320px] w-[320px] bg-[var(--color-primary)]"
          style={{ opacity: 0.18 }}
        />
        <div
          className="blob float-slow right-[-120px] top-32 h-[300px] w-[300px] bg-[var(--color-electric)]"
          style={{ animationDelay: "-3s", opacity: 0.18 }}
        />
        <div
          className="blob float-slow bottom-[-160px] left-1/3 h-[340px] w-[340px] bg-[var(--color-violet)]"
          style={{ animationDelay: "-6s", opacity: 0.18 }}
        />
        {/* Legibility wash — softens blobs behind text without flattening the palette */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 30% 40%, color-mix(in oklab, var(--background) 70%, transparent) 0%, transparent 60%)",
          }}
        />

        <div className="relative mx-auto max-w-5xl px-6 pt-16 pb-14 lg:px-10 lg:pt-24 lg:pb-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-foreground/15 bg-background/60 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-foreground/80 shadow-sm backdrop-blur-sm">
            Long-form · Reading mode
          </div>
          <h1 className="mt-5 text-[clamp(2.75rem,8vw,6rem)] leading-[0.95] tracking-tight text-foreground">
            Field Notes
          </h1>
          <div
            aria-hidden="true"
            className="mt-4 h-1 w-24 rounded-full"
            style={{
              backgroundImage:
                "linear-gradient(90deg, oklch(0.66 0.26 28), oklch(0.6 0.28 305), oklch(0.6 0.26 265))",
            }}
          />
          <p className="mt-6 max-w-2xl text-lg text-foreground/75">
            Practical notes on new technology, cybersecurity, blockchain, crypto, Web3, privacy,
            decentralization, AI workflows, and digital systems.
          </p>
        </div>
      </section>

      {/* Posts section — dark reading mode */}
      <section className="relative isolate overflow-hidden min-h-[65vh] bg-foreground text-background">
        <DarkSectionBackdrop />
        <div className="relative mx-auto max-w-5xl px-6 py-16 lg:py-24">
          {/* Search */}
          <div className="mb-8">
            <label className="relative block">
              <span className="sr-only">Search posts</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/70" />
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search posts, tags, topics…"
                className="w-full rounded-full border border-white/15 bg-white/[0.04] py-2.5 pl-10 pr-10 text-sm text-white placeholder:text-white/90 backdrop-blur transition-colors focus:border-white/40 focus:outline-none"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => setSearchInput("")}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-white/60 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </label>
          </div>
          {categories.length > 0 && (
            <div className="mb-10 flex flex-wrap items-center gap-2">
              <span className="mr-1 font-mono text-[10px] uppercase tracking-[0.25em] text-white/75">
                Categories:
              </span>
              <button
                type="button"
                onClick={() => setActiveCat(null)}
                className={
                  "rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors " +
                  (activeCat === null
                    ? "border-white bg-white text-foreground"
                    : "border-white/20 text-white/60 hover:border-white hover:text-white")
                }
              >
                View all
              </button>
              {categories.map((c) => {
                const active = activeCat === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setActiveCat(active ? null : c.id)}
                    className={
                      "rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors " +
                      (active
                        ? "border-white bg-white text-foreground"
                        : "border-white/20 text-white/60 hover:border-white hover:text-white")
                    }
                  >
                    {c.name}
                  </button>
                );
              })}
            </div>
          )}
          {visiblePosts.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-white/15 bg-white/[0.02] px-6 py-20 text-center">
              <SearchX className="h-10 w-10 text-white/70" />
              <h2 className="mt-4 font-display text-2xl text-white">No posts found</h2>
              <p className="mt-2 max-w-md text-sm text-white/60">
                {search.trim()
                  ? `Nothing matches “${search}” in this view.`
                  : "There are no posts in this category yet."}
              </p>
              <button
                type="button"
                onClick={() => {
                  setActiveCat(null);
                  setSearchInput("");
                  setSearch("");
                }}
                className="mt-6 rounded-full border border-white/30 px-5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/80 transition-colors hover:border-white hover:text-white"
              >
                View all posts
              </button>
            </div>
          ) : (
            <>
              {featured && (
                <article className="group relative mb-14 rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-md transition-all duration-300 hover:border-white/20 hover:bg-white/[0.06] focus-within:ring-1 focus-within:ring-[var(--color-primary)]/40 sm:p-8">
                  <span
                    aria-hidden="true"
                    className="nudge-right pointer-events-none absolute -left-12 top-1/2 hidden -translate-y-1/2 lg:flex"
                  >
                    <ArrowRight className="h-8 w-8 text-[var(--color-primary)] drop-shadow-[0_0_14px_color-mix(in_oklab,var(--color-primary)_55%,transparent)]" />
                  </span>
                  <div className="grid gap-x-8 gap-y-3 lg:grid-cols-[1.4fr_1fr]">
                    <div className="flex flex-col">
                      <h2
                        style={{ fontFamily: "Montserrat, sans-serif" }}
                        className="glow-violet-hover inline-flex origin-left items-center gap-2.5 text-3xl font-bold uppercase tracking-tight text-white transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1 hover:scale-[1.02] lg:text-4xl"
                      >
                        <Megaphone
                          aria-hidden="true"
                          className="h-7 w-7 lg:h-8 lg:w-8 text-[var(--color-primary)] drop-shadow-[0_0_10px_color-mix(in_oklab,var(--color-primary)_55%,transparent)]"
                        />
                        Featured Blog Post
                      </h2>
                      <div
                        aria-hidden="true"
                        className="gradient-sweep mt-3 h-0.5 w-full rounded-full"
                        style={{
                          backgroundImage:
                            "linear-gradient(90deg, oklch(0.66 0.26 28), oklch(0.6 0.28 305), oklch(0.6 0.26 265), oklch(0.6 0.28 305), oklch(0.66 0.26 28))",
                        }}
                      />
                    </div>
                    <span className="hidden lg:block" aria-hidden="true" />
                    {featured.cover_image_url && (
                      <div>
                        <Link
                          to="/blog/$slug"
                          params={{ slug: featured.slug }}
                          viewTransition
                          className="block"
                          preload="intent"
                        >
                          <div className="card-img-sweep relative rounded-md bg-white/5">
                            <img
                              src={transformSupabaseImage(featured.cover_image_url, {
                                width: 1280,
                                format: "webp",
                                quality: 78,
                              })}
                              srcSet={supabaseSrcSet(featured.cover_image_url, [480, 800, 1280])}
                              alt={featured.title}
                              loading="eager"
                              fetchPriority="high"
                              decoding="async"
                              width={1280}
                              height={800}
                              sizes="(min-width: 1024px) 720px, 100vw"
                              className="vt-cover relative h-auto w-full rounded-md object-contain transition-transform duration-500 ease-out group-hover:scale-[1.03] group-hover:shadow-[0_24px_64px_-12px_rgba(0,0,0,0.55)]"
                            />
                          </div>
                        </Link>
                        {(featured.category || featured.secondary_category) && (
                          <div className="mt-4 flex flex-wrap gap-1.5">
                            {featured.category && (
                              <button
                                type="button"
                                onClick={() => setActiveCat(featured.category_id)}
                                className="inline-flex items-center rounded-full border border-white/30 bg-white/5 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/80 transition-colors hover:border-white hover:text-white"
                                aria-label={`Filter by ${featured.category.name}`}
                              >
                                {featured.category.name}
                              </button>
                            )}
                            {featured.secondary_category && (
                              <button
                                type="button"
                                onClick={() => setActiveCat(featured.secondary_category_id)}
                                className="inline-flex items-center rounded-full border border-white/30 bg-white/5 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/80 transition-colors hover:border-white hover:text-white"
                                aria-label={`Filter by ${featured.secondary_category.name}`}
                              >
                                {featured.secondary_category.name}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex flex-col">
                      <Link
                        to="/blog/$slug"
                        params={{ slug: featured.slug }}
                        viewTransition
                        className="block"
                        preload="intent"
                      >
                        <h2 className="glow-violet-hover origin-left font-display text-3xl tracking-tight text-white transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1 hover:scale-[1.02] lg:text-4xl">
                          {highlight(featured.title)}
                        </h2>
                        {featured.excerpt && (
                          <p className="mt-3 text-base text-white/75">
                            {highlight(featured.excerpt)}
                          </p>
                        )}
                      </Link>
                      <Link
                        to="/about"
                        className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/70 transition-colors hover:text-white"
                      >
                        <img
                          src={scottAvatar}
                          alt=""
                          width={28}
                          height={28}
                          loading="lazy"
                          decoding="async"
                          className="h-7 w-7 rounded-full border border-white/20 object-cover"
                        />
                        <span className="text-[11px] font-medium tracking-[0.18em] uppercase">
                          Scott Cunningham
                        </span>
                      </Link>
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">
                        {featured.published_at && (
                          <time dateTime={featured.published_at}>
                            {formatDate(featured.published_at)}
                          </time>
                        )}
                        {featured.published_at && <span aria-hidden="true">·</span>}
                        <span>
                          {readingTimeMinutes(featured.content || featured.excerpt || "")} min read
                        </span>
                      </div>
                    </div>
                  </div>
                </article>
              )}
              {paginate && (
                <PageNav page={currentPage} totalPages={totalPages} onChange={goToPage} />
              )}
              {gridPosts.length > 0 && (
                <h2 className="mb-6 font-mono text-[10px] uppercase tracking-[0.25em] text-white/75">
                  {featured ? "Recent posts" : "Posts"}
                </h2>
              )}
              <ul className="grid gap-10 md:grid-cols-2">
                {pagedGridPosts.map((p, idx) => {
                  const eager = idx < 2;
                  return (
                    <li key={p.id}>
                      <article className="group">
                        {p.cover_image_url && (
                          <div className="relative">
                            <Link
                              to="/blog/$slug"
                              params={{ slug: p.slug }}
                              viewTransition
                              className="block"
                              preload="intent"
                            >
                              <div className="card-img-sweep flex aspect-[16/10] items-center justify-center overflow-hidden rounded-md bg-white/5">
                                <img
                                  src={transformSupabaseImage(p.cover_image_url, {
                                    width: 800,
                                    format: "webp",
                                    quality: 78,
                                  })}
                                  srcSet={supabaseSrcSet(p.cover_image_url, [480, 800, 1280])}
                                  alt={p.title}
                                  width={800}
                                  height={500}
                                  loading={eager ? "eager" : "lazy"}
                                  decoding="async"
                                  fetchPriority={eager ? "high" : "low"}
                                  sizes="(min-width: 768px) 480px, 100vw"
                                  className="max-h-full max-w-full object-contain transition-transform group-hover:scale-105"
                                />
                              </div>
                            </Link>
                            {p.category && (
                              <div className="absolute left-3 top-3 z-10">
                                <button
                                  type="button"
                                  onClick={() => setActiveCat(p.category_id)}
                                  className="inline-flex items-center rounded-full border border-white/30 bg-black/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/80 backdrop-blur-sm transition-colors hover:border-white hover:text-white"
                                  aria-label={`Filter by ${p.category.name}`}
                                >
                                  {p.category.name}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="mt-4 flex flex-nowrap items-center gap-x-2 overflow-hidden font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">
                          <Link
                            to="/about"
                            className="whitespace-nowrap transition-colors hover:text-white"
                          >
                            By Scott Cunningham
                          </Link>
                          {p.published_at && (
                            <>
                              <span aria-hidden="true">·</span>
                              <time dateTime={p.published_at} className="whitespace-nowrap">
                                {formatDate(p.published_at)}
                              </time>
                            </>
                          )}
                          <span aria-hidden="true">·</span>
                          <span className="whitespace-nowrap">
                            {readingTimeMinutes(p.content || p.excerpt || "")} min
                          </span>
                        </div>
                        <Link
                          to="/blog/$slug"
                          params={{ slug: p.slug }}
                          viewTransition
                          className="block"
                          preload="intent"
                        >
                          <h2 className="glow-violet-hover mt-2 font-display text-2xl tracking-tight text-white transition-all duration-300 ease-out origin-left hover:translate-x-1 hover:scale-[1.01]">
                            {highlight(p.title)}
                          </h2>
                          {p.excerpt && (
                            <p className="mt-2 text-sm text-white/75">{highlight(p.excerpt)}</p>
                          )}
                        </Link>
                        {p.tags && p.tags.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-x-2 gap-y-1">
                            {p.tags.slice(0, 3).map((t) => (
                              <Link
                                key={t}
                                to="/blog/tags"
                                hash={slugifyTag(t)}
                                className="inline-flex items-center rounded-full border border-white/20 bg-white/[0.04] px-3 py-1 text-xs font-medium text-white/80 transition-colors hover:border-white hover:text-white"
                              >
                                {formatTag(t)}
                              </Link>
                            ))}
                          </div>
                        )}
                      </article>
                    </li>
                  );
                })}
              </ul>
              {paginate && (
                <div className="mt-10">
                  <PageNav page={currentPage} totalPages={totalPages} onChange={goToPage} />
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <SparkleDivider />
    </div>
  );
}

function PageNav({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
}) {
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
  return (
    <nav aria-label="Blog pagination" className="mb-10 flex items-center justify-center gap-2">
      <button
        type="button"
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
        aria-label="Previous page"
        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-white/70 transition-colors hover:border-white hover:text-white disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-white/20 disabled:hover:text-white/70"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      {pages.map((p) => {
        const active = p === page;
        return (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            aria-current={active ? "page" : undefined}
            className={
              "inline-flex h-9 min-w-9 items-center justify-center rounded-full border px-3 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors " +
              (active
                ? "border-white bg-white text-foreground"
                : "border-white/20 text-white/70 hover:border-white hover:text-white")
            }
          >
            {p}
          </button>
        );
      })}
      <button
        type="button"
        onClick={() => onChange(page + 1)}
        disabled={page >= totalPages}
        aria-label="Next page"
        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-white/70 transition-colors hover:border-white hover:text-white disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-white/20 disabled:hover:text-white/70"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </nav>
  );
}
